import React from 'react';
import { enumValue } from '~/util';
import { NotePCMFoc, NotePCMQuality, NotePCMRisk } from '~/components/Patient/Notes/NotePCM';
import { reminderStatusOptions } from '~/util/reminderUtils';

export interface NoteTitleProps {
    note: NoteType;
    gap?: null | GapType | RiskGapType;
    entityId: number;
    enableOpenModal?: boolean;
}
export function NoteTitle({ note, gap, entityId, enableOpenModal = false }: NoteTitleProps) {
    function isGapNoteType() {
        const validTypes = [
            enumValue(['note', 'type', 'Risk Gap Note']),
            enumValue(['note', 'type', 'FOC Gap Note']),
            enumValue(['note', 'type', 'Quality Gap Note']),
        ];

        return validTypes.indexOf(note.type) > -1;
    }

    function getGapTypeName(gapType: null | number) {
        switch (gapType) {
            case enumValue(['gap', 'type', 'Risk Gap']):
                return 'Attestation';
            case enumValue(['gap', 'type', 'FOC Gap']):
                return 'AW';
            case enumValue(['gap', 'type', 'Quality Gap']):
                return 'SDS';
            default:
                return '';
        }
    }

    function formatNoteHeader() {
        if (note?.action) {
            switch (note.action) {
                case enumValue(['note', 'action', 'eSubmission document uploaded']):
                    return `attached supporting ${getGapTypeName(
                        note?.submission!.gap_type
                    )} documentation.`;
                case enumValue(['note', 'action', 'eSubmission document removed']):
                    return `removed supporting ${getGapTypeName(
                        note?.submission!.gap_type
                    )} documentation.`;
                case enumValue(['note', 'action', 'eSubmission document renamed']):
                    return `renamed supporting ${getGapTypeName(
                        note?.submission!.gap_type
                    )} documentation.`;
                case enumValue(['note', 'action', 'New gap']):
                    if (enableOpenModal) {
                        return 'created a new condition: ';
                    }
                    return 'created a new condition.';
                case enumValue(['note', 'action', 'Gap removed']):
                    if (enableOpenModal) {
                        return 'removed condition: ';
                    }
                    return 'removed condition.';
                case enumValue(['note', 'action', 'New eSubmission answer']):
                    return 'added a response.';
                case enumValue(['note', 'action', 'eSubmission answer changed']):
                    return 'updated a response.';
                case enumValue(['note', 'action', 'eSubmission answer removed']):
                    return 'cleared a response.';
                case enumValue(['note', 'action', 'eSubmission submitted']):
                    if (!note?.submission?.gap_type) {
                        return 'performed a submission.';
                    }
                    switch (note.submission.gap_type) {
                        case enumValue(['gap', 'type', 'Risk Gap']):
                            return 'performed an Attestation submission.';
                        case enumValue(['gap', 'type', 'FOC Gap']):
                            return 'performed an AW submission.';
                        case enumValue(['gap', 'type', 'Quality Gap']):
                            return 'performed an SDS submission.';
                        default:
                            return 'performed a submission.';
                    }
                case enumValue(['note', 'action', 'Patient created']):
                    return 'Patient created.';
                case enumValue(['note', 'action', 'Patient status changed']):
                    return 'Patient status updated.';
                case enumValue(['note', 'action', 'Patient pcp changed']):
                    return 'Patient PCP assignment changed.';
                case enumValue(['note', 'action', 'Gap submitted']):
                    return 'Submitted.';
                case enumValue(['note', 'action', 'Gap status changed']):
                    return 'Status updated.';
                case enumValue(['note', 'action', 'Review status expired']):
                    return 'Under Review Suppression Expired';
                case enumValue(['note', 'action', 'Task created']):
                    return 'created the reminder.';
                case enumValue(['note', 'action', 'Task edited']):
                    return 'edited the reminder.';
                case enumValue(['note', 'action', 'Task status changed']):
                    return `changed the reminder status to ${
                        reminderStatusOptions.find((x) => x.value === note.metadata?.status)
                            ?.noteLabel
                    }.`;
                case enumValue(['note', 'action', 'Referral created']):
                    return 'sent a referral.';
                case enumValue(['note', 'action', 'Event transferred from source system']):
                case enumValue(['note', 'action', 'Event transferred to Zephyr']):
                    return note.note;
                default:
                    break;
            }
        }
        if (note.type === enumValue(['note', 'type', 'Task Note'])) {
            return 'added a note.';
        }
        if (isGapNoteType()) {
            if (enableOpenModal) {
                return 'added a note on ';
            }
            return 'added a note.';
        }

        return 'created a patient note.';
    }

    function getPCM() {
        if (note.type === enumValue(['note', 'type', 'Risk Gap Note'])) {
            return (
                <>
                    <NotePCMRisk patientId={entityId} entity={note} gap={gap as RiskGapType} />.
                </>
            );
        }
        if (note.type === enumValue(['note', 'type', 'FOC Gap Note'])) {
            return (
                <>
                    <NotePCMFoc patientId={entityId} entity={note} gap={gap as GapType} />.
                </>
            );
        }
        if (note.type === enumValue(['note', 'type', 'Quality Gap Note'])) {
            return (
                <>
                    <NotePCMQuality patientId={entityId} entity={note} gap={gap as GapType} />.
                </>
            );
        }
        return null;
    }

    return (
        <>
            <span>{formatNoteHeader()}</span>
            {enableOpenModal && <>{getPCM()}</>}
        </>
    );
}
